if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        if (this._stockObserver) this._stockObserver.disconnect();
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
        this.unlockScroll();
      }

      removeStockNotice() {
        const re = /Presque épuisé|Plus que \d+|Dernières? pièces?|Stock limité/i;
        const root = this.modalContent;
        if (!root) return;

        Array.from(root.querySelectorAll('p, div, span, strong, li'))
          .filter(
            (el) =>
              re.test(el.textContent) &&
              !Array.from(el.children).some((c) => re.test(c.textContent))
          )
          .forEach((leaf) => {
            // sobe enquanto o pai só contiver este aviso (apanha a bolinha)
            let row = leaf;
            while (
              row.parentElement &&
              row.parentElement !== root &&
              row.parentElement.textContent.trim() === leaf.textContent.trim() &&
              !row.parentElement.querySelector('button, product-form, variant-selects')
            ) {
              row = row.parentElement;
            }
            if (!row.querySelector('button, product-form, variant-selects')) {
              row.style.setProperty('display', 'none', 'important');
            }
          });
      }

      fitTitle() {
        const el = this.modalContent.querySelector('.product__title a h2, .product__title a .h1');
        if (!el) return;
        const box = el.closest('.product__title') || el.parentElement;
        // medir o texto sem a largura da caixa a influenciar
        el.style.setProperty('display', 'inline-block', 'important');
        el.style.setProperty('white-space', 'nowrap', 'important');
        el.style.removeProperty('font-size');

        const available = box.clientWidth - 8; // pequena folga
        if (available <= 0) return;

        let size = parseFloat(getComputedStyle(el).fontSize);
        let guard = 0;
        while (el.getBoundingClientRect().width > available && size > 10 && guard < 80) {
          size -= 0.5;
          el.style.setProperty('font-size', size + 'px', 'important');
          guard++;
        }
      }

      lockScroll() {
        if (this._locked) return;
        this._scrollY = window.scrollY;
        const s = document.body.style;
        s.position = 'fixed';
        s.top = `-${this._scrollY}px`;
        s.left = '0';
        s.right = '0';
        s.width = '100%';
        this._locked = true;
      }

      unlockScroll() {
        if (!this._locked) return;
        const s = document.body.style;
        s.position = '';
        s.top = '';
        s.left = '';
        s.right = '';
        s.width = '';
        window.scrollTo({ top: this._scrollY, behavior: 'instant' });
        this._locked = false;
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            // cvd: garante que as bolinhas são criadas no modal
            const gallery = this.modalContent.querySelector('media-gallery');
            if (gallery && typeof gallery.initDots === 'function') {
              requestAnimationFrame(() => gallery.initDots());
            }

            super.show(opener);
            this.lockScroll();
            requestAnimationFrame(() => this.fitTitle());
            this.removeStockNotice();
            if (this._stockObserver) this._stockObserver.disconnect();
            this._stockObserver = new MutationObserver(() => this.removeStockNotice());
            this._stockObserver.observe(this.modalContent, { childList: true, subtree: true, characterData: true });
            setTimeout(() => this.fitTitle(), 150);
            setTimeout(() => this.fitTitle(), 500);
            if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.fitTitle());
            if (!this._fitBound) {
              this._fitBound = true;
              window.addEventListener('resize', () => this.fitTitle());
            }
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // cvd: a barra fixa e o botão de topo não pertencem ao modal
        productElement
          .querySelectorAll(
            '.cvd-sticky-bar, .cvd-scroll-top, script[src*="cvd-sticky-bar"], link[href*="cvd-sticky-bar"]'
          )
          .forEach((el) => el.remove());

        // cvd: modal limpo (só título, preço, variantes, botão e "Voir les détails")
        productElement
          .querySelectorAll('.price__info-badges, .price__info-badge')
          .forEach((el) => el.remove());

        // cvd: remover as 3 linhas (livraison, garantie, paiement) pelo texto
        const infoRe = /Livraison gratuite avec|Garantie de remboursement|Paiement 100/i;
        Array.from(productElement.querySelectorAll('span, p, div, li, strong'))
          .filter((el) => infoRe.test(el.textContent) && !Array.from(el.children).some((c) => infoRe.test(c.textContent)))
          .forEach((leaf) => {
            const row = leaf.closest('.price__info-badge, li, p, div') || leaf;
            row.remove();
          });

        const info = productElement.querySelector('.product__info-container');
        if (info) {
          const keepSelector = '.product__title, [id^="price-"], variant-selects, .product__view-details';
          Array.from(info.children).forEach((child) => {
            const hasForm = child.matches('product-form') || child.querySelector('product-form');
            if (!child.matches(keepSelector) && !hasForm) {
              child.remove();
              return;
            }
            // dentro do bloco do botão, tirar tudo o que não seja o formulário
            if (hasForm && !child.matches('product-form')) {
              Array.from(child.children).forEach((c) => {
                if (!c.matches('product-form') && !c.querySelector('product-form')) c.remove();
              });
            }
          });
          info
            .querySelectorAll('.shopify-payment-button, .product-form__quantity, shopify-accelerated-checkout')
            .forEach((el) => el.remove());
        }
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
